import React, { useState } from "react";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const InviteMemberModal = ({ projectId, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        username: "",
        role: "viewer",
    });
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Search for users as they type (only if not clearly an email being typed)
        if (name === "username" && value.length >= 2) {
            searchUsers(value);
        } else {
            setSearchResults([]);
        }
    };

    const searchUsers = async (query) => {
        try {
            setSearching(true);
            const response = await API.get(`/accounts/users/?search=${query}`);
            setSearchResults(response.data.slice(0, 5));
        } catch (err) {
            console.error("Error searching users:", err);
        } finally {
            setSearching(false);
        }
    };

    const selectUser = (user) => {
        setFormData((prev) => ({ ...prev, username: user.username }));
        setSearchResults([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const input = formData.username.trim();
        if (!input) {
            toast.error("Username or email is required.");
            return;
        }

        // Send as email if it looks like one, otherwise as username
        const payload = isEmail(input)
            ? { email: input, role: formData.role }
            : { username: input, role: formData.role };

        try {
            setLoading(true);
            const response = await API.post(`/projects/${projectId}/invite/`, payload);
            if (response.data.placeholder) {
                toast.success(response.data.message);
            } else {
                toast.success(`Invitation sent to ${input}`);
            }
            onSuccess();
        } catch (err) {
            console.error("Error inviting member:", err);
            toast.error(err.response?.data?.error || "Failed to send invitation.");
        } finally {
            setLoading(false);
        }
    };

    const inputValue = formData.username.trim();
    const isNewEmail = isEmail(inputValue) && searchResults.length === 0 && !searching;

    return (
        <div
            className="modal d-block"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Invite Team Member</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="mb-3 position-relative">
                                <label className="form-label">
                                    Username or Email <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="Enter username or email"
                                    autoComplete="off"
                                    autoFocus
                                />
                                {/* Search Results Dropdown */}
                                {searchResults.length > 0 && (
                                    <div
                                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                                        style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}
                                    >
                                        {searchResults.map((user) => (
                                            <div
                                                key={user.id}
                                                className="p-2 border-bottom cursor-pointer hover-bg-light"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => selectUser(user)}
                                            >
                                                <strong>{user.username}</strong>
                                                {user.first_name && (
                                                    <span className="text-muted ms-2">
                                                        ({user.first_name} {user.last_name})
                                                    </span>
                                                )}
                                                <br />
                                                <small className="text-muted">{user.email}</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {searching && (
                                    <div className="position-absolute w-100 bg-white border rounded p-2 text-muted">
                                        Searching...
                                    </div>
                                )}
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Role</label>
                                <select
                                    className="form-select"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                >
                                    <option value="viewer">Viewer - Can view data only</option>
                                    <option value="admin">Admin - Can manage project</option>
                                </select>
                                <small className="text-muted">
                                    {formData.role === "viewer" &&
                                        "Viewers can see all project data but cannot make changes."}
                                    {formData.role === "admin" &&
                                        "Admins can create studies, manage datasets, and invite/remove members."}
                                </small>
                            </div>
                            {isNewEmail ? (
                                <div className="alert alert-warning">
                                    <small>
                                        <strong>New user:</strong> A placeholder account will be created for{" "}
                                        <strong>{inputValue}</strong>. They will gain full access when
                                        they register with this email address.
                                    </small>
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    <small>
                                        The user will receive an invitation that they can accept or
                                        decline. They will gain access to the project only after
                                        accepting.
                                    </small>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                        Sending...
                                    </>
                                ) : isNewEmail ? (
                                    "Create & Add Member"
                                ) : (
                                    "Send Invitation"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InviteMemberModal;
